/**
 * MortgageDonutChart
 *
 * Displays mortgage payment breakdown as an animated donut chart
 * with legend showing each component's contribution.
 */

import { PieChart, Pie, Cell } from 'recharts';
import type { MortgageBreakdown } from '@/types/mortgage';
import { MORTGAGE_CHART_COLORS, MORTGAGE_BREAKDOWN_LABELS } from '@/types/mortgage';
import { formatPrice } from '@/utils';

// Fixed pixel size — matches the `w-48 h-48` (12rem = 192px) wrapper
// below. We bypass <ResponsiveContainer> on purpose: it relies on a
// ResizeObserver that fires after the first paint, so the chart
// briefly renders at -1×-1 and Recharts logs a warning. The container
// is a known constant here, so explicit dimensions are simpler.
const CHART_PX = 192;

interface MortgageDonutChartProps {
    breakdown: MortgageBreakdown;
}

interface ChartDataItem {
    name: string;
    value: number;
    color: string;
    label: string;
}

export function MortgageDonutChart({ breakdown }: MortgageDonutChartProps) {
    const chartData: ChartDataItem[] = [
        {
            name: 'principalAndInterest',
            value: breakdown.principalAndInterest,
            color: MORTGAGE_CHART_COLORS.principalAndInterest,
            label: MORTGAGE_BREAKDOWN_LABELS.principalAndInterest,
        },
        {
            name: 'taxes',
            value: breakdown.taxes,
            color: MORTGAGE_CHART_COLORS.taxes,
            label: MORTGAGE_BREAKDOWN_LABELS.taxes,
        },
        {
            name: 'mortgageInsurance',
            value: breakdown.mortgageInsurance,
            color: MORTGAGE_CHART_COLORS.mortgageInsurance,
            label: MORTGAGE_BREAKDOWN_LABELS.mortgageInsurance,
        },
        {
            name: 'others',
            value: breakdown.others,
            color: MORTGAGE_CHART_COLORS.others,
            label: MORTGAGE_BREAKDOWN_LABELS.others,
        },
    ].filter((item) => item.value > 0);

    const hasData = chartData.length > 0 && breakdown.total > 0;

    return (
        <div className="flex flex-col items-center">
            {/* Donut Chart */}
            <div className="relative w-48 h-48">
                {hasData ? (
                    <PieChart width={CHART_PX} height={CHART_PX}>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={75}
                            paddingAngle={2}
                            dataKey="value"
                            animationBegin={0}
                            animationDuration={1000}
                        >
                            {chartData.map((entry) => (
                                <Cell key={entry.name} fill={entry.color} />
                            ))}
                        </Pie>
                    </PieChart>
                ) : (
                    <div className="w-full h-full rounded-full border-8 border-gray-200" />
                )}

                {/* Center Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-bold text-gray-900">
                        {formatPrice(breakdown.total)}
                    </span>
                    <span className="text-sm text-gray-500">Monthly</span>
                </div>
            </div>

            {/* Legend */}
            <div className="mt-6 space-y-2 w-full max-w-xs">
                <LegendItem
                    color={MORTGAGE_CHART_COLORS.principalAndInterest}
                    label={MORTGAGE_BREAKDOWN_LABELS.principalAndInterest}
                    value={breakdown.principalAndInterest}
                />
                <LegendItem
                    color={MORTGAGE_CHART_COLORS.taxes}
                    label={MORTGAGE_BREAKDOWN_LABELS.taxes}
                    value={breakdown.taxes}
                />
                <LegendItem
                    color={MORTGAGE_CHART_COLORS.mortgageInsurance}
                    label={MORTGAGE_BREAKDOWN_LABELS.mortgageInsurance}
                    value={breakdown.mortgageInsurance}
                />
                <LegendItem
                    color={MORTGAGE_CHART_COLORS.others}
                    label={MORTGAGE_BREAKDOWN_LABELS.others}
                    value={breakdown.others}
                />
            </div>
        </div>
    );
}

interface LegendItemProps {
    color: string;
    label: string;
    value: number;
}

function LegendItem({ color, label, value }: LegendItemProps) {
    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                />
                <span className="text-sm text-gray-600">{label}</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
                {formatPrice(value)}
            </span>
        </div>
    );
}
